import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom'; // <-- added for portal
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import WorkerAssignmentModal from '../WorkerAssignmentModal/WorkerAssignmentModal';

import {
  Bell,
  BarChart3,
  MapPin,
  Users,
  Trash2,
  CheckCircle,
  Clock,
  AlertTriangle,
  AlertCircle,
  Image,
  MessageSquare,
  Star,
  FileText,
  LogOut,
  User
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [binsData, setBinsData] = useState({ total: 0 });
  const [workersData, setWorkersData] = useState({ active: 0 });
  const [reportsData, setReportsData] = useState({
    total: 0,
    pending: 0,
    resolved: 0,
    overdue: 0
  });
  const [timelineData, setTimelineData] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(true);
  const [assignmentModal, setAssignmentModal] = useState({ isOpen: false, report: null });

  useEffect(() => {
    const fetchAllData = async () => {
      const token = localStorage.getItem('binbuddy_token');
        
      if (!token) {
        setLoading(false);
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Helper: Safely determine array length regardless of API response structure
      // This fixes the issue where production might return data wrapped differently
      const getCount = (response, key) => {
        const data = response.data;
        if (!data) return 0;
        // If data is directly an array
        if (Array.isArray(data)) return data.length;
        // If data is inside the expected key (e.g., { bins: [...] })
        if (data[key] && Array.isArray(data[key])) return data[key].length;
        // If data is wrapped in a generic 'data' key
        if (data.data && Array.isArray(data.data)) return data.data.length;
        return 0;
      };

      try {
        // 1. Fetch Bins (Independent Try/Catch)
        try {
          const binsRes = await axios.get('/api/bins', config);
          setBinsData({ total: getCount(binsRes, 'bins') });
        } catch (err) {
          console.error('Error fetching bins:', err);
          // Keep default 0
        }

        // 2. Fetch Workers (Independent Try/Catch)
        try {
          const workersRes = await axios.get('/api/workers', config);
          setWorkersData({ active: getCount(workersRes, 'workers') });
        } catch (err) {
          console.error('Error fetching workers:', err);
          // Keep default 0
        }
        
        // 3. Fetch Report Stats (Independent Try/Catch)
        try {
          const allReportsRes = await axios.get('/api/reports', config);
          // Handle both { reports: [...] } and plain [...] structures
          const allReports = allReportsRes.data.reports || (Array.isArray(allReportsRes.data) ? allReportsRes.data : []);
          
          if (Array.isArray(allReports)) {
            setReportsData({
              total: allReports.length,
              pending: allReports.filter(r => r.status === 'pending').length,
              resolved: allReports.filter(r => r.status === 'resolved').length,
              overdue: allReports.filter(r => r.status === 'overdue').length
            });
          }
        } catch (err) {
          console.error('Error fetching report stats:', err);
        }

        // 4. Fetch Recent Reports List (Independent Try/Catch)
        try {
          const recentReportsRes = await axios.get('/api/reports?limit=5', config);
          setRecentReports(recentReportsRes.data.reports || (Array.isArray(recentReportsRes.data) ? recentReportsRes.data : []));
        } catch (err) {
          console.error('Error fetching recent reports:', err);
        }
        
        // 5. Timeline Data (Mock)
        setTimelineData([
          { day: 'Mon', tasks: 12 },
          { day: 'Tue', tasks: 19 },
          { day: 'Wed', tasks: 15 },
          { day: 'Thu', tasks: 22 },
          { day: 'Fri', tasks: 18 },
          { day: 'Sat', tasks: 14 },
          { day: 'Sun', tasks: 10 }
        ]);
        setTimelineLoading(false);

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        // Even if global error occurs, we try to keep whatever loaded
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-red-500 mb-4">⚠️</div>
          <p className="text-gray-600">Error loading dashboard: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Handle report actions
  const handleReportAction = async (reportId, action, report = null) => {
    try {
      const token = localStorage.getItem('binbuddy_token');
      
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
      if (action === 'assign') {
        // Open worker assignment modal instead of direct assignment
        const reportToAssign = report || recentReports.find(r => r._id === reportId);
        setAssignmentModal({ isOpen: true, report: reportToAssign });
        return;
      }
      
      let endpoint = '';
      let payload = {};
      
      switch (action) {
        case 'acknowledge':
          endpoint = `/api/reports/${reportId}/status`;
          payload = { status: 'acknowledged' };
          break;
        default:
          return;
      }
      
      await axios.patch(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh reports
      await refreshReports();
    } catch (error) {
      console.error('Error handling report action:', error);
    }
  };

  // Refresh reports helper function
  const refreshReports = async () => {
    try {
      const token = localStorage.getItem('binbuddy_token');
      // This refreshes the *recent* reports list
      const reportsRes = await axios.get('/api/reports?limit=5', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecentReports(reportsRes.data.reports || []);

      // Also refresh the stats cards
      try {
        const allReportsRes = await axios.get('/api/reports', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const allReports = allReportsRes.data.reports || [];
        if (Array.isArray(allReports)) {
          setReportsData({
            total: allReports.length,
            pending: allReports.filter(r => r.status === 'pending').length,
            resolved: allReports.filter(r => r.status === 'resolved').length,
            overdue: allReports.filter(r => r.status === 'overdue').length
          });
        }
      } catch (err) {
        console.error('Error refreshing report stats:', err);
      }

    } catch (error) {
      console.error('Error refreshing reports:', error);
    }
  };

  // Handle successful worker assignment
  const handleAssignmentSuccess = (assignmentData) => {
    // console.log('Worker assigned successfully:', assignmentData);
    // Refresh reports to show updated status
    refreshReports();
    // You could also show a success toast here
  };

  // Close assignment modal
  const closeAssignmentModal = () => {
    setAssignmentModal({ isOpen: false, report: null });
  };

  // Generate notifications from recent reports
  const getNotifications = () => {
    const notifications = [];
    
    recentReports.slice(0, 4).forEach(report => {
      const timeAgo = new Date(report.createdAt);
      const now = new Date();
      const diffHours = Math.floor((now - timeAgo) / (1000 * 60 * 60));
      const timeText = diffHours < 1 ? 'Just now' : 
                      diffHours < 24 ? `${diffHours}h ago` : 
                      `${Math.floor(diffHours / 24)}d ago`;
      
      notifications.push({
        type: report.priority === 'urgent' ? 'urgent' : 'report',
        message: `${report.issueType.replace('_', ' ')} reported in ${report.location?.address || 'Unknown location'}`,
        time: timeText
      });
    });
    
    // Add some default notifications if no reports
    if (notifications.length === 0) {
      notifications.push(
        { type: 'photo', message: 'Worker uploaded collection proof', time: '15m ago' },
        { type: 'complaint', message: 'System running smoothly', time: '1h ago' }
      );
    }
    
    return notifications;
  };

  const mockTopWorkers = [
    { name: 'John Doe', tasks: 45, rating: 4.8 },
    { name: 'Jane Smith', tasks: 42, rating: 4.7 },
    { name: 'Mike Johnson', tasks: 38, rating: 4.6 }
  ];

  const mockRecentActions = [
    { type: 'bin', message: 'New bin registered in Sector 9', time: '2h ago', route: '/bin-management' },
    { type: 'task', message: 'Task assigned to John Doe', time: '3h ago', route: '/task-management' },
    { type: 'scan', message: 'QR Code scanned at Location A', time: '4h ago', route: '/qr-scanner' }
  ];

  const quickStats = [
    { label: 'Total Bins', value: loading ? '...' : binsData.total.toString(), icon: <Trash2 />, route: '/management/bins' },
    { label: 'Pending Reports', value: loading ? '...' : reportsData.pending.toString(), icon: <AlertTriangle />, route: '/management/reports' },
    { label: 'Total Reports', value: loading ? '...' : reportsData.total.toString(), icon: <FileText />, route: '/management/reports' },
    { label: 'Resolved Reports', value: loading ? '...' : reportsData.resolved.toString(), icon: <CheckCircle />, route: '/management/reports' },
    { label: 'Active Workers', value: loading ? '...' : workersData.active.toString(), icon: <Users />, route: '/management/workers' }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      // No need to show success message here as it's already handled in the AuthContext
    } catch (error) {
      console.error('Logout error:', error);
      // Don't show error toast as the logout will redirect to login page
    }
  };

  /* ---------- Portal helper component (mounts children to document.body) ---------- */
  const Portal = ({ children }) => {
    const [container] = useState(() => {
      const el = document.createElement('div');
      el.setAttribute('data-portal', 'worker-assignment');
      return el;
    });

    useEffect(() => {
      document.body.appendChild(container);
      return () => {
        if (container && container.parentNode) container.parentNode.removeChild(container);
      };
    }, [container]);

    return ReactDOM.createPortal(children, container);
  };
  /* ------------------------------------------------------------------------------- */

  return (
    <div className="admin-dashboard">
      {/* Admin Header */}
      <div className="admin-header">
        <div className="admin-header-content">
          <div className="admin-info">
            <h1 className="dashboard-title">Admin Dashboard</h1>
          </div>
        </div>
      </div>
      {/* Quick Stats Info */}
      <div className="quick-tip">Monitor real-time bin status and worker performance through the dashboard.</div>

      {/* Quick Stats Grid */}
      <div className="stats-grid">
        {quickStats.map((stat, index) => (
          <div key={index} className="stat-card display-only">
            <div className="stat-icon">{stat.icon}</div>
            <div className="stat-details">
              <h3>{stat.value}</h3>
              <p>{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tasks Timeline - Full Width */}
      <div className="dashboard-card timeline-card full-width">
        <div className="card-header">
          <h2><BarChart3 size={20} /> Tasks Timeline</h2>
        </div>
        <div className="chart-container">
          {timelineLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-2"></div>
                <p className="text-gray-600">Loading timeline...</p>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timelineData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="tasks" stroke="#279e0a" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Recent Reports Section */}
      <div className="dashboard-card recent-reports" style={{ marginBottom: '4rem', marginTop: '2rem' }}>
        <div className="card-header">
          <h2><AlertTriangle size={20} /> Recent User Reports - Quick Actions</h2>
          <div className="reports-header-actions">
            <span className="reports-count">
              {recentReports.length > 0 ? `${recentReports.length} recent reports` : 'No new reports'}
            </span>
            <Link to="/management/reports" className="view-all-link">
              View All Reports
            </Link>
          </div>
        </div>
        <div className="reports-summary">
          {loading ? (
            <p>Loading reports...</p>
          ) : recentReports.length > 0 ? (
            <div className="reports-grid enhanced">
              {recentReports.slice(0, 5).map((report, index) => (
                <div key={index} className="report-summary-card">
                  <div className={`priority-badge ${report.priority}`}>
                    {report.priority}
                  </div>
                  <h4>{report.issueType.replace('_', ' ')}</h4>
                  <p className="report-location">{report.location?.address || 'Unknown location'}</p>
                  <p className="report-time">
                    {new Date(report.createdAt).toLocaleDateString()}
                  </p>
                  <div className={`status-badge ${report.status}`}>
                    {report.status}
                  </div>
                  <div className="report-actions">
                        {report.status === 'pending' && (
                          <>
                            <button 
                              className="action-btn acknowledge"
                              onClick={() => handleReportAction(report._id, 'acknowledge')}
                              title="Acknowledge Report"
                            >
                              <CheckCircle size={16} /> Acknowledge
                            </button>
                            <button 
                              className="action-btn assign"
                              onClick={() => handleReportAction(report._id, 'assign', report)}
                              title="Assign Worker"
                            >
                              <Users size={16} /> Assign
                            </button>
                          </>
                        )}
                        {report.status === 'acknowledged' && (
                          <button 
                            className="action-btn assign"
                            onClick={() => handleReportAction(report._id, 'assign', report)}
                            title="Assign Worker"
                          >
                            <Users size={16} /> Assign Worker
                          </button>
                        )}
                    {(report.status === 'in_progress' || report.status === 'resolved') && (
                      <span className="status-info">
                        {report.status === 'in_progress' ? '🔄 In Progress' : '✅ Resolved'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-reports">No reports yet. Users can scan QR codes to report issues.</p>
          )}
        </div>
      </div>

      {/* Worker Assignment Modal mounted via Portal so it sits above all page content */}
      {assignmentModal.isOpen && (
        <Portal>
          <WorkerAssignmentModal
            isOpen={assignmentModal.isOpen}
            onClose={closeAssignmentModal}
            report={assignmentModal.report}
            onAssignSuccess={handleAssignmentSuccess}
          />
        </Portal>
      )}
    </div>
  );
};

export default AdminDashboard;